import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl, getAuthToken } from '../../services/api';
import './UserSections.css';

const API_BASE_URL = getApiBaseUrl();

const UserSectionSubscription = ({ subscriptionData: propSubscriptionData, refreshSubscription, onTrialActivated }) => {
  const { t } = useTranslation('user');
  const [activeTab, setActiveTab] = useState('current-plan');
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [trialEligible, setTrialEligible] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState(null);
  const [trialModalOpen, setTrialModalOpen] = useState(false);
  const [trialTrack, setTrialTrack] = useState('educational'); // 'educational' | 'exam'
  const [trialLanguage, setTrialLanguage] = useState('english');
  const [trialYearLevelGroups, setTrialYearLevelGroups] = useState([]);
  const [trialYearLevels, setTrialYearLevels] = useState([]); // legacy flat list
  const [trialSelectedYearId, setTrialSelectedYearId] = useState(null);
  const [trialExamTypes, setTrialExamTypes] = useState([]);
  const [trialSelectedExamCode, setTrialSelectedExamCode] = useState('');
  const [trialSubmitting, setTrialSubmitting] = useState(false);
  const [trialError, setTrialError] = useState(null);

  const trialLanguages = [
    { value: 'english', label: 'English' },
    { value: 'arabic', label: 'Arabic' },
    { value: 'french', label: 'French' },
    { value: 'spanish', label: 'Spanish' },
    { value: 'german', label: 'German' },
    { value: 'italian', label: 'Italian' }
  ];

  const token = getAuthToken();

  const mapApiSubscriptions = (dataSubscriptions) => {
    if (!Array.isArray(dataSubscriptions) || dataSubscriptions.length === 0) return [];
    return dataSubscriptions.map(d => {
      const endsAt = d.ends_at ? new Date(d.ends_at) : null;
      return {
        id: d.subscription_id,
        planName: d.plan_name,
        planId: d.plan_id,
        status: d.is_active ? 'active' : (d.status || 'expired'),
        price: d.price_at_purchase,
        currency: d.currency_at_purchase || 'DA',
        billingCycle: (d.duration_days >= 365 ? 'yearly' : 'monthly'),
        startDate: d.starts_at,
        endDate: d.ends_at,
        nextBillingDate: endsAt ? endsAt.toLocaleDateString() : null,
        autoRenewal: false,
        features: ['Educational content access', 'Vocabulary & practice', 'Progress tracking'],
        is_trial: !!d.is_trial
      };
    });
  };

  // Use prop data when provided (from UserDashboard), otherwise fetch
  useEffect(() => {
    if (propSubscriptionData) {
      setSubscriptions(mapApiSubscriptions(propSubscriptionData.subscriptions));
      setTrialEligible(!!propSubscriptionData.trialEligible);
      setLoading(false);
      return;
    }
    const fetchSubscription = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API_BASE_URL}/api/me/subscription`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          setSubscriptions(mapApiSubscriptions(data.subscriptions || []));
          setTrialEligible(!!data.trialEligible);
        } else {
          setSubscriptions([]);
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
        setError('Failed to load subscription data');
      } finally {
        setLoading(false);
      }
    };
    fetchSubscription();
  }, [token, propSubscriptionData]);

  useEffect(() => {
    if (!trialModalOpen || !token) return;
    const fetchYearLevels = async () => {
      try {
        const url = `${API_BASE_URL}/api/me/trial/year-levels${trialLanguage ? `?language=${encodeURIComponent(trialLanguage)}` : ''}`;
        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          setTrialYearLevelGroups(data.yearLevelGroups || []);
          setTrialYearLevels(data.yearLevels || []);
        } else {
          setTrialYearLevelGroups([]);
          setTrialYearLevels([]);
        }
      } catch (err) {
        setTrialYearLevelGroups([]);
        setTrialYearLevels([]);
      }
    };
    if (trialTrack === 'educational') fetchYearLevels();
  }, [trialModalOpen, token, trialLanguage, trialTrack]);

  useEffect(() => {
    if (!trialModalOpen || !token) return;
    if (trialTrack !== 'exam') return;
    const fetchExamTypes = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/me/trial/exam-types`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          setTrialExamTypes(data.examTypes || []);
        } else {
          setTrialExamTypes([]);
        }
      } catch (err) {
        setTrialExamTypes([]);
      }
    };
    fetchExamTypes();
  }, [trialModalOpen, token, trialTrack]);

  // Fetch data based on active tab
  useEffect(() => {
    const fetchTabData = async () => {
      if (activeTab === 'payment-methods') {
        try {
          const response = await fetch(`${API_BASE_URL}/api/subscriptions/payment-methods`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setPaymentMethods(data.paymentMethods || []);
            }
          }
        } catch (err) {
          console.error('Error fetching payment methods:', err);
        }
      } else if (activeTab === 'billing-history') {
        try {
          const response = await fetch(`${API_BASE_URL}/api/subscriptions/billing-history`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setBillingHistory(data.billingHistory || []);
            }
          }
        } catch (err) {
          console.error('Error fetching billing history:', err);
        }
      } else if (activeTab === 'invoices') {
        try {
          const response = await fetch(`${API_BASE_URL}/api/subscriptions/invoices`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setInvoices(data.invoices || []);
            }
          }
        } catch (err) {
          console.error('Error fetching invoices:', err);
        }
      }
    };

    fetchTabData();
  }, [activeTab, token]);

  const tabs = [
    { id: 'current-plan', label: t('user.subscription.tabs.currentPlan'), icon: 'subscriptions' },
    { id: 'payment-methods', label: t('user.subscription.tabs.paymentMethods'), icon: 'credit_card' },
    { id: 'billing-history', label: t('user.subscription.tabs.billingHistory'), icon: 'receipt' },
    { id: 'invoices', label: t('user.subscription.tabs.invoices'), icon: 'description' },
    { id: 'special-offers', label: t('user.subscription.tabs.specialOffers'), icon: 'local_offer' }
  ];

  const defaultFeatures = [
    t('user.subscription.currentSubscription.features.unlimitedAccess'),
    t('user.subscription.currentSubscription.features.liveSessions'),
    t('user.subscription.currentSubscription.features.advancedExercises'),
    t('user.subscription.currentSubscription.features.prioritySupport'),
    t('user.subscription.currentSubscription.features.certificate'),
    t('user.subscription.currentSubscription.features.mobileApp')
  ];

  const specialOffers = [
    {
      id: 1,
      title: 'Refer a Friend',
      description: 'Get 1 free month for each friend who signs up',
      type: 'referral',
      status: 'active',
      code: 'REFER2024',
      validUntil: 'Dec 31, 2024',
      friendsReferred: 3,
      freeMonthsEarned: 3,
      referralLink: 'https://nativelike.com/ref/john123'
    },
    {
      id: 2,
      title: 'Holiday Special',
      description: '50% off your first 3 months',
      type: 'seasonal',
      status: 'active',
      code: 'HOLIDAY50',
      validUntil: 'Jan 15, 2025',
      countdownDays: 23,
      discount: '50%',
      duration: '3 months'
    },
    {
      id: 3,
      title: 'Annual Discount',
      description: 'Save 20% with annual billing',
      type: 'billing',
      status: 'active',
      code: 'ANNUAL20',
      validUntil: 'Jan 31, 2025',
      discount: '20%',
      savings: '$71.88'
    }
  ];

  const promoRedemptions = [
    {
      id: 1,
      code: 'WELCOME10',
      description: '10% off first month',
      appliedDate: 'Nov 15, 2024',
      status: 'applied',
      savings: '$3.00'
    },
    {
      id: 2,
      code: 'ANNUAL20',
      description: '20% off annual billing',
      appliedDate: 'Nov 10, 2024',
      status: 'applied',
      savings: '$71.88'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'current-plan':
        return (
          <div className="subscription-tab-content">
            <div className="subscription-header">
              <h3>{t('user.subscription.currentSubscription.title')}</h3>
              <button className="subscription-btn primary">
                <span className="material-icons">settings</span>
                {t('user.subscription.currentSubscription.managePlan')}
              </button>
            </div>

            {loading ? (
              <div className="subscription-loading">
                <div className="spinner"></div>
                <p>{t('user.subscription.loading')}</p>
              </div>
            ) : subscriptions.length > 0 ? (
              <div className="subscription-plan-cards">
                {subscriptions.map((sub, index) => (
                  <div key={sub.id != null ? sub.id : `sub-${index}`} className="subscription-plan-card">
                    <div className="plan-header">
                      <h4>{sub.planName}</h4>
                      <span className={`subscription-status ${(sub.status || 'active').toLowerCase()}`}>
                        {sub.status || t('user.subscription.currentSubscription.active')}
                      </span>
                    </div>
                    <div className="plan-price">
                      <span className="amount">{sub.currency}{sub.price ?? '—'}</span>
                      <span className="cycle">/{sub.billingCycle === 'yearly' ? t('user.subscription.currentSubscription.year') : t('user.subscription.currentSubscription.month')}</span>
                    </div>
                    <div className="plan-features">
                      <h5>{t('user.subscription.currentSubscription.planFeatures')}</h5>
                      <ul>
                        {(sub.features || defaultFeatures).map((feature, i) => (
                          <li key={i}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="plan-details">
                      <div className="detail-item">
                        <span className="label">{t('user.subscription.currentSubscription.nextBilling')}</span>
                        <span className="value">{sub.nextBillingDate || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">{t('user.subscription.currentSubscription.autoRenewal')}</span>
                        <span className="value">{sub.autoRenewal ? t('user.subscription.currentSubscription.enabled') : t('user.subscription.currentSubscription.disabled')}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="subscription-empty-state">
                <i className="material-icons">subscriptions</i>
                <p>{t('user.subscription.noSubscription')}</p>
                {trialEligible && (
                  <div className="trial-cta">
                    <button
                      type="button"
                      className="subscription-btn primary trial-btn"
                      onClick={() => {
                        setTrialError(null);
                        setTrialSelectedYearId(null);
                        setTrialSelectedExamCode('');
                        setTrialTrack('educational');
                        setTrialLanguage('english');
                        setTrialModalOpen(true);
                      }}
                    >
                      Start Free Trial
                    </button>
                    <p className="trial-cta-subtext">Try free for 7 days — Educational levels or International exams</p>
                  </div>
                )}
              </div>
            )}

            {trialModalOpen && (
              <div className="modal-overlay" onClick={() => !trialSubmitting && setTrialModalOpen(false)} role="dialog" aria-modal="true" aria-labelledby="trial-modal-title">
                <div className="modal-content trial-modal" onClick={e => e.stopPropagation()}>
                  <h3 id="trial-modal-title">Start Free Trial</h3>
                  {trialError && <p className="trial-modal-error">{trialError}</p>}
                  {/* Step 1: Language */}
                  <div className="trial-modal-step trial-step-language">
                    <span className="trial-step-badge">Step 1</span>
                    <label className="trial-modal-label" htmlFor="trial-language-select">Language</label>
                    <select
                      id="trial-language-select"
                      className="trial-language-select"
                      value={trialLanguage}
                      onChange={(e) => {
                        setTrialLanguage(e.target.value);
                        setTrialSelectedYearId(null);
                      }}
                      aria-label="Select language"
                    >
                      {trialLanguages.map(lang => (
                        <option key={lang.value} value={lang.value}>{lang.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Step 2: Choose trial type */}
                  <div className="trial-modal-step">
                    <span className="trial-step-badge">Step 2</span>
                    <label className="trial-modal-label">Choose trial type</label>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="radio"
                          name="trial_track"
                          value="educational"
                          checked={trialTrack === 'educational'}
                          onChange={() => {
                            setTrialTrack('educational');
                            setTrialError(null);
                            setTrialSelectedExamCode('');
                          }}
                        />
                        <span>Educational levels’ vocabulary (7 days)</span>
                      </label>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="radio"
                          name="trial_track"
                          value="exam"
                          checked={trialTrack === 'exam'}
                          onChange={() => {
                            setTrialTrack('exam');
                            setTrialError(null);
                            setTrialSelectedYearId(null);
                          }}
                        />
                        <span>International exam’s vocabulary (7 days)</span>
                      </label>
                    </div>
                  </div>

                  {trialTrack === 'educational' ? (
                    <>
                      <div className="trial-modal-step">
                        <span className="trial-step-badge">Step 3</span>
                        <label className="trial-modal-label">School level & year</label>
                      </div>
                      {trialYearLevelGroups.length === 0 && !trialSubmitting ? (
                        <p>Loading year levels…</p>
                      ) : (
                        <div className="trial-year-groups">
                          {trialYearLevelGroups.map(group => (
                            <div key={group.schoolLevel} className="trial-year-group">
                              <h4 className="trial-year-group-title">{group.schoolLabel}</h4>
                              <ul className="trial-year-list">
                                {group.years.map(yl => (
                                  <li key={yl.id}>
                                    <label>
                                      <input
                                        type="radio"
                                        name="trial_year_level"
                                        value={yl.id}
                                        checked={trialSelectedYearId === yl.id}
                                        onChange={() => setTrialSelectedYearId(yl.id)}
                                      />
                                      <span>{yl.name}</span>
                                    </label>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="trial-modal-step">
                        <span className="trial-step-badge">Step 3</span>
                        <label className="trial-modal-label" htmlFor="trial-exam-select">Select exam type</label>
                        <select
                          id="trial-exam-select"
                          className="trial-language-select"
                          value={trialSelectedExamCode}
                          onChange={(e) => setTrialSelectedExamCode(e.target.value)}
                          aria-label="Select exam type"
                        >
                          <option value="" disabled>Select…</option>
                          {trialExamTypes.map(x => (
                            <option key={x.value} value={x.value}>{x.label}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                  <div className="modal-actions">
                    <button type="button" className="subscription-btn secondary" onClick={() => setTrialModalOpen(false)} disabled={trialSubmitting}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="subscription-btn primary"
                      disabled={(trialTrack === 'educational' ? !trialSelectedYearId : !trialSelectedExamCode) || trialSubmitting}
                      onClick={async () => {
                        if (!token) return;
                        if (trialTrack === 'educational' && !trialSelectedYearId) return;
                        if (trialTrack === 'exam' && !trialSelectedExamCode) return;
                        setTrialSubmitting(true);
                        setTrialError(null);
                        try {
                          const res = await fetch(`${API_BASE_URL}/api/me/trial`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify(
                              trialTrack === 'exam'
                                ? { track: 'exam', exam_code: trialSelectedExamCode }
                                : { track: 'educational', year_level_id: trialSelectedYearId }
                            )
                          });
                          const data = await res.json().catch(() => ({}));
                          if (res.ok && data.success) {
                            setTrialModalOpen(false);
                            if (refreshSubscription) refreshSubscription();
                            if (onTrialActivated) onTrialActivated();
                          } else {
                            setTrialError(data.message || (data.code === 'TRIAL_ALREADY_USED' ? 'Free trial can only be used once.' : 'Failed to start trial.'));
                          }
                        } catch (err) {
                          setTrialError('Failed to start trial.');
                        } finally {
                          setTrialSubmitting(false);
                        }
                      }}
                    >
                      {trialSubmitting ? 'Starting…' : 'Confirm'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'payment-methods':
        return (
          <div className="subscription-tab-content">
            <div className="subscription-header">
              <h3>{t('user.subscription.paymentMethods.title')}</h3>
              <button className="subscription-btn primary">
                <span className="material-icons">add</span>
                {t('user.subscription.paymentMethods.addMethod')}
              </button>
            </div>

            <div className="payment-methods-grid">
              {paymentMethods.length === 0 ? (
                <div className="subscription-empty-state">
                  <i className="material-icons">credit_card</i>
                  <p>{t('user.subscription.noPaymentMethods')}</p>
                </div>
              ) : (
                paymentMethods.map(method => (
                  <div key={method.id} className="payment-method-card">
                    <div className="payment-header">
                      <h4>{method.type}</h4>
                      {method.isDefault && (
                        <span className="subscription-status active">{t('user.subscription.paymentMethods.default')}</span>
                      )}
                    </div>
                    
                    <div className="payment-details">
                      <div className="detail-row">
                        <span className="label">{t('user.subscription.paymentMethods.cardNumber')}</span>
                        <span className="value">{method.displayNumber || method.cardLast4 ? `**** **** **** ${method.cardLast4}` : method.paypalEmail || 'N/A'}</span>
                      </div>
                      {method.cardExpMonth && method.cardExpYear && (
                        <div className="detail-row">
                          <span className="label">{t('user.subscription.paymentMethods.expires')}</span>
                          <span className="value">{`${method.cardExpMonth}/${method.cardExpYear}`}</span>
                        </div>
                      )}
                      <div className="detail-row">
                        <span className="label">{t('user.subscription.paymentMethods.status')}</span>
                        <span className={`subscription-status ${method.isActive ? 'active' : 'inactive'}`}>
                          {method.isActive ? 'active' : 'inactive'}
                        </span>
                      </div>
                    </div>

                    <div className="payment-actions">
                      <button className="subscription-btn secondary">{t('user.subscription.paymentMethods.edit')}</button>
                      <button className="subscription-btn danger">{t('user.subscription.paymentMethods.remove')}</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );

      case 'billing-history':
        return (
          <div className="subscription-tab-content">
            <div className="subscription-header">
              <h3>{t('user.subscription.billingHistory.title')}</h3>
              <button className="subscription-btn primary">
                <span className="material-icons">download</span>
                {t('user.subscription.billingHistory.exportData')}
              </button>
            </div>

            <div className="billing-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('user.subscription.billingHistory.date')}</th>
                    <th>{t('user.subscription.billingHistory.description')}</th>
                    <th>{t('user.subscription.billingHistory.amount')}</th>
                    <th>{t('user.subscription.billingHistory.status')}</th>
                    <th>{t('user.subscription.billingHistory.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {billingHistory.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="empty-state">
                        <i className="material-icons">receipt</i>
                        <p>{t('user.subscription.noBillingHistory')}</p>
                      </td>
                    </tr>
                  ) : (
                    billingHistory.map(item => (
                      <tr key={item.id}>
                        <td>{item.paymentDate ? new Date(item.paymentDate).toLocaleDateString() : item.dueDate ? new Date(item.dueDate).toLocaleDateString() : 'N/A'}</td>
                        <td>{item.description || 'Subscription Payment'}</td>
                        <td>${item.amount?.toFixed(2) || '0.00'}</td>
                        <td>
                          <span className={`subscription-status ${item.status}`}>
                            {item.status}
                          </span>
                        </td>
                        <td>
                          <div className="table-actions">
                            <button className="subscription-btn small" title={t('user.subscription.billingHistory.viewDetails')}>
                              <span className="material-icons">visibility</span>
                            </button>
                            <button className="subscription-btn small" title={t('user.subscription.billingHistory.downloadInvoice')}>
                              <span className="material-icons">download</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'invoices':
        return (
          <div className="subscription-tab-content">
            <div className="subscription-header">
              <h3>{t('user.subscription.invoices.title')}</h3>
              <button className="subscription-btn primary">
                <span className="material-icons">email</span>
                {t('user.subscription.invoices.emailAll')}
              </button>
            </div>

            <div className="invoices-grid">
              {invoices.length === 0 ? (
                <div className="subscription-empty-state">
                  <i className="material-icons">description</i>
                  <p>{t('user.subscription.noInvoices')}</p>
                </div>
              ) : (
                invoices.map(invoice => (
                  <div key={invoice.id} className="invoice-card">
                    <div className="invoice-header">
                      <h4>{invoice.invoiceNumber || invoice.id}</h4>
                      <span className={`subscription-status ${(invoice.status || '').toLowerCase()}`}>
                        {invoice.status || '—'}
                      </span>
                    </div>
                    
                    <div className="invoice-details">
                      <div className="detail-row">
                        <span className="label">{t('user.subscription.invoices.date')}</span>
                        <span className="value">{invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString() : 'N/A'}</span>
                      </div>
                      {invoice.description && (
                        <div className="detail-row">
                          <span className="label">{t('user.subscription.invoices.description')}</span>
                          <span className="value">{invoice.description}</span>
                        </div>
                      )}
                      <div className="detail-row">
                        <span className="label">{t('user.subscription.invoices.amount')}</span>
                        <span className="value">{invoice.totalAmount != null ? Number(invoice.totalAmount).toFixed(2) : (invoice.amount != null ? Number(invoice.amount).toFixed(2) : '0.00')} {invoice.currency || ''}</span>
                      </div>
                    </div>

                    <div className="invoice-actions">
                      {invoice.pdfUrl && (
                        <>
                          <button
                            type="button"
                            className="subscription-btn secondary"
                            onClick={() => window.open(invoice.pdfUrl, '_blank')}
                          >
                            {t('user.subscription.invoices.view')}
                          </button>
                          <button
                            type="button"
                            className="subscription-btn secondary"
                            onClick={() => window.open(invoice.pdfUrl, '_blank', 'noopener')}
                          >
                            {t('user.subscription.invoices.download')}
                          </button>
                        </>
                      )}
                      {!invoice.pdfUrl && (
                        <span className="invoice-no-file">{t('user.subscription.invoices.view')}</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );

      case 'special-offers':
        return (
          <div className="subscription-tab-content">
            <div className="subscription-header">
              <h3>{t('user.subscription.specialOffers.title')}</h3>
              <button className="subscription-btn primary">
                <span className="material-icons">share</span>
                {t('user.subscription.specialOffers.shareOffers')}
              </button>
            </div>

            {/* Referral Program Section */}
            <div className="special-offers-section">
              <h4>{t('user.subscription.specialOffers.referralProgram')}</h4>
              <div className="referral-stats">
                <div className="stat-card">
                  <h5>{t('user.subscription.specialOffers.friendsReferred')}</h5>
                  <span className="stat-number">3</span>
                </div>
                <div className="stat-card">
                  <h5>{t('user.subscription.specialOffers.freeMonthsEarned')}</h5>
                  <span className="stat-number">3</span>
                </div>
                <div className="stat-card">
                  <h5>{t('user.subscription.specialOffers.totalSavings')}</h5>
                  <span className="stat-number">$89.97</span>
                </div>
              </div>

              <div className="referral-link-section">
                <h5>{t('user.subscription.specialOffers.yourReferralLink')}</h5>
                <div className="link-container">
                  <input 
                    type="text" 
                    value="https://nativelike.com/ref/john123" 
                    readOnly 
                    className="referral-input"
                  />
                  <button className="subscription-btn secondary">
                    <span className="material-icons">content_copy</span>
                    {t('user.subscription.specialOffers.copy')}
                  </button>
                </div>
                <p className="referral-description">
                  {t('user.subscription.specialOffers.shareDescription')}
                </p>
              </div>
            </div>

            {/* Special Offers Grid */}
            <div className="special-offers-section">
              <h4>{t('user.subscription.specialOffers.availableOffers')}</h4>
              <div className="special-offers-grid">
                {specialOffers.map(offer => (
                  <div key={offer.id} className={`offer-card ${offer.type}`}>
                    <div className="offer-header">
                      <h5>{offer.title}</h5>
                      <span className={`offer-badge ${offer.type}`}>
                        {offer.type === 'referral' ? t('user.subscription.specialOffers.referral') : 
                         offer.type === 'seasonal' ? t('user.subscription.specialOffers.seasonal') : t('user.subscription.specialOffers.billing')}
                      </span>
                    </div>
                    
                    <p className="offer-description">{offer.description}</p>
                    
                    <div className="offer-details">
                      <div className="offer-code">
                        <span className="label">{t('user.subscription.specialOffers.code')}</span>
                        <span className="code">{offer.code}</span>
                      </div>
                      <div className="offer-validity">
                        <span className="label">{t('user.subscription.specialOffers.validUntil')}</span>
                        <span className="date">{offer.validUntil}</span>
                      </div>
                      {offer.type === 'seasonal' && (
                        <div className="countdown-timer">
                          <span className="label">{t('user.subscription.specialOffers.timeRemaining')}</span>
                          <span className="countdown">{offer.countdownDays} {t('user.subscription.specialOffers.days')}</span>
                        </div>
                      )}
                      {offer.type === 'referral' && (
                        <div className="referral-progress">
                          <span className="label">{t('user.subscription.specialOffers.progress')}</span>
                          <span className="progress">{offer.friendsReferred} {t('user.subscription.specialOffers.friendsReferredText')}</span>
                        </div>
                      )}
                    </div>

                    <div className="offer-actions">
                      <button className="subscription-btn primary">
                        {offer.type === 'referral' ? t('user.subscription.specialOffers.shareLink') : t('user.subscription.specialOffers.applyCode')}
                      </button>
                      {offer.type === 'referral' && (
                        <button className="subscription-btn secondary">
                          <span className="material-icons">share</span>
                          {t('user.subscription.specialOffers.share')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Promo Redemption Status */}
            <div className="special-offers-section">
              <h4>{t('user.subscription.specialOffers.appliedPromoCodes')}</h4>
              <div className="promo-redemptions">
                {promoRedemptions.map(promo => (
                  <div key={promo.id} className="promo-redemption-card">
                    <div className="promo-header">
                      <h5>{promo.code}</h5>
                      <span className={`subscription-status ${promo.status}`}>
                        {promo.status}
                      </span>
                    </div>
                    
                    <div className="promo-details">
                      <div className="detail-row">
                        <span className="label">{t('user.subscription.specialOffers.description')}</span>
                        <span className="value">{promo.description}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">{t('user.subscription.specialOffers.appliedDate')}</span>
                        <span className="value">{promo.appliedDate}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">{t('user.subscription.specialOffers.savings')}</span>
                        <span className="value savings">{promo.savings}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="subscription-section">
        {/* Tabs Container */}
      <div className="subscription-tabs">
        <div className="subscription-tabs-list">
            {tabs.map(tab => (
              <button
                key={tab.id}
              className={`subscription-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="material-icons">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
      <div className="subscription-content">
          {renderTabContent()}
      </div>
    </div>
  );
};

export default UserSectionSubscription; 